/**
 * DiscrepancyPanel — Panel de discrepancias y casos pendientes (F9.03)
 *
 * Muestra los casos detectados agrupados por severidad con opción de
 * colapsar/expandir. Incluye adapter ProcessedInfrastructure[] → DiscrepancyReport.
 *
 * @version 1.0.0
 * @task F9.03
 */

import React, { useState } from 'react';
import { colors, typography, borderRadius } from '@/design-system/v2';
import {
  generateDiscrepancyReport,
  getPendingCases,
  type DiscrepancyReport,
  type DiscrepancyCase,
  type ProcedenciaCaso,
  type Severity,
} from '@/services/pipeline/DiscrepancyReportService';
import { avisoCentroide, deriveProcedencia } from '@/utils/procedenciaVia';
import type { BatchAggregateReport, MunicipalityResult } from '@/services/pipeline/MunicipalityBatchProcessor';
import type { PipelineOutput } from '@/services/pipeline/CoordinateProcessingPipeline';
import type { ProcessedInfrastructure } from '@/types/processing';

// ============================================================================
// ADAPTER: ProcessedInfrastructure[] → DiscrepancyReport
// ============================================================================

/** Construye un BatchAggregateReport mínimo desde infraestructuras del wizard */
function buildBatchReport(
  infras: ProcessedInfrastructure[],
  municipio: string,
  ine5: string,
  provincia: string,
): BatchAggregateReport {
  const outputs: PipelineOutput[] = infras.map((i): PipelineOutput => ({
    id: i.id,
    nombre: i.nombre,
    coordenadasFinales: (i.xFinal && i.yFinal)
      ? { x: i.xFinal, y: i.yFinal }
      : null,
    crs: 'EPSG:25830',
    scoreConfianza: i.score,
    flagValidacion: i.crossValidation?.flag ?? 'SIN_COORDENADAS',
    // Si hay coords finales pero no hay crossValidation, asumir fuente 'documento'
    // para evitar falso positivo 'sin-coordenadas' en el servicio
    fuenteCoordenadas: (i.xFinal && i.yFinal)
      ? ((i.crossValidation?.fuenteCoordenadas ?? 'documento') as 'documento' | 'referencia' | 'ninguna')
      : 'ninguna',
    casoValidacion: i.crossValidation?.caso ?? 'C',
    enriquecimiento: {
      codigoINE: ine5,
      matchDERA: false,
      scoreDERA: 0,
      tipologiaFinal: i.tipo,
    },
    geocodificacion: {
      exitoso: Boolean(i.xFinal && i.yFinal),
      fuente: i.source ?? '',
      confianza: i.score / 100,
      nombreEncontrado: null,
    },
    // DISDUP: distancia documento↔referencia del comparador (REVISION_MANUAL la
    // trae siempre); el servicio la usa en el detalle de `conflicto-referencia`.
    distanciaMetros: i.crossValidation?.distanciaMetros ?? null,
    codigoPostal: null,
    codigoPostalSource: null,
    codigoPostalWarning: null,
    validacionCentroide: null,
    errores: [],
    tiempoProcesamiento: 0,
  }));

  const munResult: MunicipalityResult = {
    ine5,
    nombre: municipio,
    provincia,
    status: infras.length > 0 ? 'success' : 'skipped',
    infraestructuras: {
      total: infras.length,
      validadas: infras.filter(i => Boolean(i.xFinal && i.yFinal)).length,
      // OBS-FN-02-A: «geocodificadas» = «filas resueltas». REVISION_MANUAL
      // (status='manual') conserva las coords del documento y VALIDADO
      // (status='original') las confirma; ambas cuentan como resueltas
      // a efectos de cobertura, igual que en pickInfrasForDisplay.isResolved.
      geocodificadas: infras.filter(i => i.status === 'geocoded' || i.status === 'manual' || i.status === 'original').length,
      sinCoordenadas: infras.filter(i => !i.xFinal || !i.yFinal).length,
      conErrores: 0,
    },
    outputs,
    tiempoMs: 0,
    errores: [],
  };

  return {
    municipios: {
      total: 1,
      exitosos: munResult.status === 'success' ? 1 : 0,
      parciales: 0,
      fallidos: 0,
      omitidos: munResult.status === 'skipped' ? 1 : 0,
    },
    infraestructuras: munResult.infraestructuras,
    tiempoTotalMs: 0,
    promedioPorMunicipioMs: 0,
    resultadosPorMunicipio: [munResult],
    resumen: `${municipio} (${ine5})`,
  };
}

/**
 * Genera un DiscrepancyReport directamente desde las infraestructuras del wizard.
 */
export function buildDiscrepancyFromInfras(
  infras: ProcessedInfrastructure[],
  municipio: string = 'Municipio',
  ine5: string = '00000',
  provincia: string = 'Andalucía',
): DiscrepancyReport {
  const batchReport = buildBatchReport(infras, municipio, ine5, provincia);
  return generateDiscrepancyReport(batchReport, {
    procedencia: buildProcedenciaMap(infras),
  });
}

/**
 * CENFOR-B — procedencia por infra para el servicio (DOP-CENFORB-2=a′): el
 * `PipelineOutput` no lleva `source`, así que la vía y el aviso del centroide
 * (`avisoCentroide`, el mismo texto del entregable) se derivan aquí, donde la
 * infra sí está. `hasCoords` con las mismas x/y finales que usa el adaptador.
 */
function buildProcedenciaMap(
  infras: ProcessedInfrastructure[],
): Map<string, ProcedenciaCaso> {
  const map = new Map<string, ProcedenciaCaso>();
  for (const i of infras) {
    const hasCoords = Boolean(i.xFinal && i.yFinal);
    const via = deriveProcedencia(i, hasCoords);
    map.set(i.id, {
      via,
      aviso: via === 'centroide' ? avisoCentroide(i) : undefined,
    });
  }
  return map;
}

// ============================================================================
// HELPERS DE COLOR / SEVERIDAD / TIPO
// ============================================================================

const SEV_COLOR: Record<Severity, string> = {
  alta:  '#F87171', // rojo
  media: '#FBBF24', // naranja
  baja:  '#A3E635', // lima
};

/** Dot SVG accesible, sin dependencia de fuentes emoji */
const SevDot: React.FC<{ sev: Severity }> = ({ sev }) => (
  <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" style={{ flexShrink: 0 }}>
    <circle cx="5" cy="5" r="5" fill={SEV_COLOR[sev]} />
  </svg>
);

/** Etiquetas cortas por tipo de discrepancia para la cabecera */
const TIPO_LABEL: Record<string, string> = {
  'sin-coordenadas':       'Sin coords',
  'score-bajo':            'Score bajo',
  'centroide-municipal':   'Centroide municipal',
  // DISDUP: caso A.4 del comparador (documento y referencia a >500 m; se
  // conserva la coordenada del documento). Antes salía como «Score bajo» y,
  // por `fuenteCoordenadas: 'ninguna'`, también como «Sin coords».
  'conflicto-referencia':  'Conflicto con referencia',
  'geocodificacion-fallida': 'Geocod. fallida',
  // Heurística de posición (la coordenada coincide con el centroide), NO la
  // vía; renombrada en CENFOR-B para no confundirla con «Centroide municipal».
  'discrepancia-centroide':  'Cerca del centroide',
  'error-pipeline':          'Error',
};

/** Color por tipo (mismo sistema semántico) */
const TIPO_COLOR: Record<string, string> = {
  'sin-coordenadas':         '#F87171',
  'error-pipeline':          '#F87171',
  'score-bajo':              '#FBBF24',
  'centroide-municipal':     '#FBBF24',
  'conflicto-referencia':    '#FBBF24',
  'geocodificacion-fallida': '#FBBF24',
  'discrepancia-centroide':  '#A3E635',
};

// ============================================================================
// ESTILOS (DS v2)
// ============================================================================

const S = {
  root: {
    background: colors.bg.tertiary,
    borderRadius: borderRadius.xl,
    padding: '20px',
    marginTop: '12px',
    marginBottom: '24px', // punto 1: espacio antes de Formatos
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer' as const,
    userSelect: 'none' as const,
  },
  title: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    color: colors.fg.secondary,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.4px',
  },
  badge: (sev: Severity) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    background: `${SEV_COLOR[sev]}22`,
    color: SEV_COLOR[sev],
    borderRadius: '6px',
    padding: '2px 8px',
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.mono,
    fontWeight: typography.fontWeight.semibold,
  }),
  chevron: (open: boolean) => ({
    fontSize: '12px',
    color: colors.fg.muted,
    transform: open ? 'rotate(180deg)' : 'none',
    transition: 'transform 0.2s',
    display: 'inline-block',
  }),
  okMsg: {
    fontSize: typography.fontSize.base,
    color: colors.fg.muted,
    marginTop: '12px',
    textAlign: 'center' as const,
    padding: '8px 0',
  },
  summaryRow: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap' as const,
    marginTop: '12px',
    marginBottom: '16px',
  },
  caseCard: {
    background: colors.bg.secondary,
    borderRadius: borderRadius.lg,
    padding: '10px 14px',
    marginBottom: '8px',
    borderLeft: '3px solid',
  },
  caseHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px',
  },
  caseName: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    color: colors.fg.primary,
    flex: 1,
  },
  caseType: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.mono,
    color: colors.fg.muted,
  },
  caseDetail: {
    fontSize: typography.fontSize.sm,
    color: colors.fg.secondary,
    paddingLeft: '22px',
  },
  sectionLabel: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    color: colors.fg.secondary,
    marginBottom: '8px',
    marginTop: '8px',
    paddingBottom: '4px',
    borderBottom: `1px solid ${colors.border.DEFAULT}`,
  },
} as const;

// ============================================================================
// SUBCOMPONENTE: tarjeta de un caso
// ============================================================================

const CaseCard: React.FC<{ caso: DiscrepancyCase }> = ({ caso }) => (
  <div style={{ ...S.caseCard, borderLeftColor: SEV_COLOR[caso.severidad] }}>
    <div style={S.caseHeader}>
      <SevDot sev={caso.severidad} />
      <span style={S.caseName}>{caso.infraNombre}</span>
      <span style={S.caseType}>{caso.tipo}</span>
    </div>
    <div style={S.caseDetail}>{caso.detalle}</div>
  </div>
);

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export interface DiscrepancyPanelProps {
  report: DiscrepancyReport;
  /** Total de infraestructuras procesadas; 0 activa el empty state */
  infraCount?: number;
  /** P4REP: lleva al paso Valida (P2). Sin él no se pinta el botón:
   *  el «atrás» del navegador sacaba a la portada. */
  onGoToValida?: () => void;
}

export const DiscrepancyPanel: React.FC<DiscrepancyPanelProps> = ({ report, infraCount, onGoToValida }) => {
  const sinDatos = infraCount !== undefined && infraCount === 0;
  const [open, setOpen] = useState(false);
  const [pulseKey, setPulseKey] = useState(0);
  const pending = getPendingCases(report);
  const altas  = pending.filter(c => c.severidad === 'alta');
  const medias = pending.filter(c => c.severidad === 'media');
  const bajas  = pending.filter(c => c.severidad === 'baja');

  const _headerLabel = report.totalCasos === 0
    ? 'Sin discrepancias'
    : `${report.totalCasos} discrepancia${report.totalCasos > 1 ? 's' : ''} detectada${report.totalCasos > 1 ? 's' : ''}`;

  // Resumen por tipo para la cabecera (siempre visible)
  const tipoEntries = Object.entries(report.porTipo)
    .filter(([, n]) => n > 0)
    .sort(([a], [b]) => {
      const order: Record<string, number> = {
        'sin-coordenadas': 0, 'error-pipeline': 1,
        'geocodificacion-fallida': 2, 'centroide-municipal': 3, 'conflicto-referencia': 4,
        'score-bajo': 5, 'discrepancia-centroide': 6,
      };
      return (order[a] ?? 9) - (order[b] ?? 9);
    });

  // Punto 1: Si no hay discrepancias, no renderizar la caja
  if (!sinDatos && report.totalCasos === 0) return null;

  return (
    <div style={S.root} data-testid="discrepancy-panel">
      {/* Cabecera colapsable */}
      <div style={S.header} onClick={() => { if (sinDatos) return; const next = !open; setOpen(next); if (!next) setPulseKey(k => k + 1); }} role="button" aria-expanded={open}>
        {/* Izquierda: icono + título */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          {/* Icono advertencia alineado */}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
            stroke="#F87171" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            style={{ flexShrink: 0, display: 'block' }}>
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <span style={S.title}>Discrepancias</span>
        </div>
        {/* Derecha: pills de tipo + total + toggle */}
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, flexWrap: 'wrap' as const }}>
          {/* Grupo pills — separado 40px del totalizador */}
          {!sinDatos && report.totalCasos > 0 && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '32px' }}>
              {tipoEntries.map(([tipo, n]) => (
                <span
                  key={tipo}
                  style={{
                    background: `${TIPO_COLOR[tipo] ?? '#94A3B8'}18`,
                    color: TIPO_COLOR[tipo] ?? '#94A3B8',
                    border: `1px solid ${TIPO_COLOR[tipo] ?? '#94A3B8'}40`,
                    borderRadius: '6px',
                    padding: '2px 8px',
                    fontSize: typography.fontSize.xs,
                    fontFamily: typography.fontFamily.mono,
                    fontWeight: typography.fontWeight.semibold,
                    whiteSpace: 'nowrap' as const,
                  }}
                >
                  {n} {TIPO_LABEL[tipo] ?? tipo}
                </span>
              ))}
            </span>
          )}
          {!sinDatos && report.totalCasos > 0 && (
            <span style={{
              fontSize: typography.fontSize.sm,
              fontFamily: typography.fontFamily.mono,
              fontWeight: typography.fontWeight.semibold,
              color: altas.length > 0 ? SEV_COLOR.alta : medias.length > 0 ? SEV_COLOR.media : colors.fg.muted,
            }}>
              {report.totalCasos} total
            </span>
          )}
          {!sinDatos && (
            <div
              key={open ? 'open' : `closed-${pulseKey}`}
              className={open ? '' : 'ptel-toggle-pulse'}
              style={{
                width: '32px', height: '32px', display: 'flex', alignItems: 'center',
                justifyContent: 'center', background: colors.bg.secondary,
                border: `1px solid ${colors.border.DEFAULT}`, borderRadius: '8px',
                flexShrink: 0,
              }}
            >
              <svg
                viewBox="0 0 24 24" fill="none" stroke={colors.fg.secondary}
                strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                style={{ width: '14px', height: '14px', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
              >
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </div>
          )}
        </span>
      </div>

      {/* Empty state: sin infraestructuras */}
      {sinDatos && (
        <div style={{
          textAlign: 'center',
          padding: '24px 16px',
          color: colors.fg.muted,
          fontSize: typography.fontSize.base,
        }}>
          Sin datos suficientes para detectar discrepancias.
          <br />
          <span style={{ fontSize: typography.fontSize.sm, opacity: 0.7 }}>
            El documento no contiene infraestructuras detectadas.
          </span>
        </div>
      )}

      {/* CTA 'Cómo resolverlas' + acceso directo a Valida (P2) */}
      {!sinDatos && report.totalCasos > 0 && (
        <div style={{
          background: 'rgba(45, 212, 191, 0.06)',
          border: '1px solid rgba(45, 212, 191, 0.2)',
          borderRadius: '8px',
          padding: '10px 14px',
          marginTop: '12px',
          marginBottom: open ? '12px' : 0,
          fontSize: typography.fontSize.sm,
          color: colors.fg.secondary,
          lineHeight: 1.7,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' as const }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontWeight: typography.fontWeight.semibold, color: colors.primary }}>Cómo resolverlas:</span>
              {' '}las infraestructuras «sin coordenadas» pueden geocodificarse en
              {' '}<strong style={{ color: colors.fg.primary }}>Valida</strong>
              {' '}usando el visor de mapa o la búsqueda por dirección.
              Los «score bajo» indican coordenadas poco fiables — conviene verificarlas
              visualmente antes de exportar.
            </div>
            {/* Acceso directo a Valida (P2) — punto 3 */}
            {onGoToValida && (
            <button
              type="button"
              onClick={e => { e.stopPropagation(); onGoToValida(); }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '5px',
                padding: '5px 12px', borderRadius: '6px', border: `1px solid ${colors.primary}`,
                background: 'rgba(45,212,191,0.08)', color: colors.primary,
                fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold,
                cursor: 'pointer', whiteSpace: 'nowrap' as const, flexShrink: 0,
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
              Volver a Valida
            </button>
            )}
          </div>
        </div>
      )}

      {/* Contenido colapsable (solo si hay datos) */}
      {!sinDatos && open && (
        <>
          {report.totalCasos === 0 ? (
            <div style={S.okMsg}>✅ Todos los registros procesados son válidos.</div>
          ) : (
            <>
              {/* Resumen de conteos */}
              <div style={S.summaryRow}>
                {altas.length > 0  && <span style={S.badge('alta')} ><SevDot sev="alta"  /> {altas.length} alta{altas.length  > 1 ? 's' : ''}</span>}
                {medias.length > 0 && <span style={S.badge('media')}><SevDot sev="media" /> {medias.length} media{medias.length > 1 ? 's' : ''}</span>}
                {bajas.length > 0  && <span style={S.badge('baja')} ><SevDot sev="baja"  /> {bajas.length} baja{bajas.length  > 1 ? 's' : ''}</span>}
              </div>

              {/* Casos por sección de severidad */}
              {altas.length > 0 && (
                <>
                  <div style={S.sectionLabel}>Prioridad alta</div>
                  {altas.map((c, i) => <CaseCard key={`alta-${i}`} caso={c} />)}
                </>
              )}
              {medias.length > 0 && (
                <>
                  <div style={S.sectionLabel}>Prioridad media</div>
                  {medias.map((c, i) => <CaseCard key={`med-${i}`} caso={c} />)}
                </>
              )}
              {bajas.length > 0 && (
                <>
                  <div style={S.sectionLabel}>Prioridad baja</div>
                  {bajas.map((c, i) => <CaseCard key={`baja-${i}`} caso={c} />)}
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};
